import { useState, useEffect } from 'react'
import {
  Check,
  X,
  Zap,
  HelpCircle,
  Crown,
  MonitorPlay,
  FolderOpen,
  ArrowRight,
  Info,
} from 'lucide-react'

interface SubscriptionModalProps {
  isOpen: boolean
  onClose: () => void
  onSwitchToPaymentPack?: () => void
}

type BillingCycle = 'monthly' | 'yearly'

const PLAN_PRICES = {
  monthly: {
    pro: { monthly: 35, billed: 420 },
    insider: { monthly: 95, billed: 1140 },
    elite: { monthly: 295, billed: 3540 },
  },
  yearly: {
    pro: { monthly: 28, billed: 336 },
    insider: { monthly: 76, billed: 912 },
    elite: { monthly: 236, billed: 2832 },
  },
}

const PAYMENT_LINKS = {
  monthly: {
    pro: 'https://buy.stripe.com/test_cNibJ1aLXcpX7ike031ZS00',
    insider: 'https://buy.stripe.com/test_3cIaEX4nzcpX3245tx1ZS01',
    elite: 'https://buy.stripe.com/test_eVq8wP6vHfC97ikf471ZS02',
  },
  yearly: {
    pro: 'https://buy.stripe.com/test_14A7sL07jgGd8mo5tx1ZS03',
    insider: 'https://buy.stripe.com/test_bJeaEXdY949H8mobRV1ZS04',
    elite: 'https://buy.stripe.com/test_eVqbJ14nzahP324aNR1ZS05',
  },
}

const FEATURES = {
  free: [
    {
      text: 'Free trial with one time 80 credits',
      icon: Zap,
      color: 'text-yellow-500',
      fill: true,
    },
    { text: '1G storage', icon: Check, color: 'text-green-500' },
    { text: 'Creating videos using chat', icon: Check, color: 'text-green-500' },
    { text: 'AI Generative features', icon: Check, color: 'text-green-500' },
    { text: 'Video Editor', icon: Check, color: 'text-green-500' },
    { text: 'Collaborative video editing tools', icon: Check, color: 'text-green-500' },
    { text: 'Top-up credits', icon: X, color: 'text-gray-300' },
    { text: 'Remove Watermark', icon: X, color: 'text-gray-300' },
  ],
  paidCommon: [
    { text: 'Creating videos using chat', icon: Check, color: 'text-green-500' },
    { text: 'AI Generative features', icon: Check, color: 'text-green-500' },
    { text: 'Video Editor', icon: Check, color: 'text-green-500' },
    { text: 'Collaborative video editing tools', icon: Check, color: 'text-green-500' },
    { text: 'Top-up credits', icon: Check, color: 'text-green-500' },
    { text: 'Remove Watermark', icon: Check, color: 'text-green-500' },
  ],
}

export default function SubscriptionModal({
  isOpen,
  onClose,
  onSwitchToPaymentPack,
}: SubscriptionModalProps) {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('yearly')
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  })

  useEffect(() => {
    // Set target date to 2 days from now for the demo/prototype
    const targetDate = new Date()
    targetDate.setDate(targetDate.getDate() + 2)

    const timer = setInterval(() => {
      const now = new Date().getTime()
      const distance = targetDate.getTime() - now

      if (distance < 0) {
        clearInterval(timer)
        return
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative h-[90vh] w-[90vw] max-w-[1400px] overflow-y-auto rounded-3xl bg-white shadow-2xl transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 cursor-pointer p-2 text-gray-400 transition-colors hover:text-gray-600"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="p-4 lg:p-8">
          {/* Header */}
          <div className="mb-6 flex flex-col items-center">
            <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900">
              Unlock more value
            </h2>

            <div className="relative mt-5 flex w-full justify-center">
              {/* Billing Toggle */}
              <div className="flex items-center rounded-full bg-gray-100 p-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`cursor-pointer rounded-full px-6 py-2 text-sm font-medium transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`flex cursor-pointer items-center gap-2 rounded-full px-6 py-2 text-sm font-medium transition-all ${
                    billingCycle === 'yearly'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Yearly
                  <span className="rounded-full bg-indigo-500 px-2 py-0.5 text-[10px] font-bold text-white">
                    Save 20%
                  </span>
                </button>
              </div>

              {/* Top-up credits button (Desktop) */}
              <div className="absolute right-0 top-1/2 hidden -translate-y-1/2 lg:block">
                <button
                  onClick={onSwitchToPaymentPack}
                  className="flex cursor-pointer items-center gap-2 rounded-xl bg-gradient-to-r from-yellow-200 to-yellow-100 px-5 py-2 text-sm font-semibold text-yellow-800 transition hover:from-yellow-300 hover:to-yellow-200"
                >
                  <Zap className="h-4 w-4 fill-yellow-800" />
                  Top-up credits
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Top-up credits button (Mobile) */}
            <div className="mt-4 lg:hidden">
              <button
                onClick={onSwitchToPaymentPack}
                className="flex cursor-pointer items-center gap-2 rounded-xl bg-gradient-to-r from-yellow-200 to-yellow-100 px-5 py-2 text-sm font-semibold text-yellow-800 transition hover:from-yellow-300 hover:to-yellow-200"
              >
                <Zap className="h-4 w-4 fill-yellow-800" />
                Top-up credits
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Main Grid */}
          <div className="flex flex-col gap-5 xl:flex-row">
            {/* Free Plan */}
            <div className="flex-1 rounded-2xl border border-gray-200 p-5 xl:max-w-[280px]">
              <h3 className="text-xl font-bold text-gray-900">Free</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900">$0</span>
                <span className="text-sm text-gray-500">/month</span>
              </div>

              <div className="mt-5 space-y-3">
                {FEATURES.free.map((feature, index) => (
                  <div key={index} className="flex gap-2.5 text-xs text-gray-600">
                    <feature.icon
                      className={`h-4 w-4 flex-shrink-0 ${feature.color} ${feature.fill ? 'fill-current' : ''}`}
                    />
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Paid Plans Container */}
            <div className="flex-3 flex flex-col overflow-hidden rounded-2xl border-2 border-[#4F46E5]">
              {/* Special Offer Banner */}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-[#4F46E5] px-4 py-2 text-sm text-white">
                <div className="flex items-center gap-2 font-medium">
                  <span>🎉 SPECIAL OFFER</span>
                  <span className="rounded bg-white/20 px-2 py-0.5 text-xs">Extra 10% off</span>
                </div>
                {/* Countdown Timer */}
                <div className="flex items-center gap-1 font-mono text-xs">
                  <span className="rounded bg-white/20 px-1.5 py-0.5">
                    {timeLeft.days.toString().padStart(2, '0')[0]}
                  </span>
                  <span className="rounded bg-white/20 px-1.5 py-0.5">
                    {timeLeft.days.toString().padStart(2, '0')[1]}
                  </span>
                  <span className="mx-0.5">d</span>
                  <span className="rounded bg-white/20 px-1.5 py-0.5">
                    {timeLeft.hours.toString().padStart(2, '0')[0]}
                  </span>
                  <span className="rounded bg-white/20 px-1.5 py-0.5">
                    {timeLeft.hours.toString().padStart(2, '0')[1]}
                  </span>
                  <span>:</span>
                  <span className="rounded bg-white/20 px-1.5 py-0.5">
                    {timeLeft.minutes.toString().padStart(2, '0')[0]}
                  </span>
                  <span className="rounded bg-white/20 px-1.5 py-0.5">
                    {timeLeft.minutes.toString().padStart(2, '0')[1]}
                  </span>
                  <span>:</span>
                  <span className="rounded bg-white/20 px-1.5 py-0.5">
                    {timeLeft.seconds.toString().padStart(2, '0')[0]}
                  </span>
                  <span className="rounded bg-white/20 px-1.5 py-0.5">
                    {timeLeft.seconds.toString().padStart(2, '0')[1]}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 divide-y divide-gray-200 bg-white md:grid-cols-3 md:divide-x md:divide-y-0">
                {/* Pro Plan */}
                <div className="p-5">
                  <h3 className="text-xl font-bold text-gray-900">Pro</h3>
                  <div className="mt-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-gray-900">
                        ${PLAN_PRICES[billingCycle].pro.monthly}
                      </span>
                      <span className="text-sm text-gray-500">/month</span>
                    </div>
                    <div className="mt-1 text-[10px] text-gray-400">
                      Billed ${PLAN_PRICES[billingCycle].pro.billed} {billingCycle}
                    </div>
                  </div>

                  <button
                    onClick={() => window.open(PAYMENT_LINKS[billingCycle].pro, '_blank')}
                    className="mt-5 w-full cursor-pointer rounded-lg bg-[#1C1C1E] py-2.5 text-xs font-semibold text-white transition hover:bg-gray-800"
                  >
                    Get pro
                  </button>

                  <div className="mt-5 space-y-3">
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <Zap className="h-4 w-4 flex-shrink-0 fill-current text-yellow-500" />
                      <span className="font-bold">700 credits/month</span>
                    </div>
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <MonitorPlay className="h-4 w-4 flex-shrink-0 text-blue-500" />
                      <div className="flex items-center gap-1">
                        <span>≈ 3 mins Full AI video</span>
                        <Info className="h-3 w-3 text-gray-400" />
                      </div>
                    </div>
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <FolderOpen className="h-4 w-4 flex-shrink-0 text-blue-500" />
                      <div className="flex items-center gap-1">
                        <span>≈ 30 mins with Stock video</span>
                        <Info className="h-3 w-3 text-gray-400" />
                      </div>
                    </div>
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                      <span>200G storage</span>
                    </div>
                    {FEATURES.paidCommon.map((feature, i) => (
                      <div key={i} className="flex gap-2.5 text-xs text-gray-600">
                        <feature.icon className={`h-4 w-4 flex-shrink-0 ${feature.color}`} />
                        <span>{feature.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Insider Plan */}
                <div className="p-5">
                  <h3 className="text-xl font-bold text-gray-900">Insider</h3>
                  <div className="mt-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-gray-900">
                        ${PLAN_PRICES[billingCycle].insider.monthly}
                      </span>
                      <span className="text-sm text-gray-500">/month</span>
                    </div>
                    <div className="mt-1 text-[10px] text-gray-400">
                      Billed ${PLAN_PRICES[billingCycle].insider.billed} {billingCycle}
                    </div>
                  </div>

                  <button
                    onClick={() => window.open(PAYMENT_LINKS[billingCycle].insider, '_blank')}
                    className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-[#1C1C1E] py-2.5 text-xs font-semibold text-white transition hover:bg-gray-800"
                  >
                    Get insider <ArrowRight className="h-3.5 w-3.5" />
                  </button>

                  <div className="mt-5 space-y-3">
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <Zap className="h-4 w-4 flex-shrink-0 fill-current text-yellow-500" />
                      <span className="font-bold">2400 credits/month</span>
                    </div>
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <MonitorPlay className="h-4 w-4 flex-shrink-0 text-blue-500" />
                      <div className="flex items-center gap-1">
                        <span>≈ 10 mins Full AI video</span>
                        <Info className="h-3 w-3 text-gray-400" />
                      </div>
                    </div>
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <FolderOpen className="h-4 w-4 flex-shrink-0 text-blue-500" />
                      <div className="flex items-center gap-1">
                        <span>≈ 100 mins with Stock video</span>
                        <Info className="h-3 w-3 text-gray-400" />
                      </div>
                    </div>
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                      <span>Unlimited storage</span>
                    </div>
                    {FEATURES.paidCommon.map((feature, i) => (
                      <div key={i} className="flex gap-2.5 text-xs text-gray-600">
                        <feature.icon className={`h-4 w-4 flex-shrink-0 ${feature.color}`} />
                        <span>{feature.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Elite Plan */}
                <div className="relative overflow-hidden p-5">
                  <div className="absolute right-0 top-0">
                    <div className="translate-x-8 translate-y-3 rotate-45 bg-[#1C1C1E] px-8 py-1 text-[9px] font-bold text-white shadow-sm">
                      Best value
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900">Elite</h3>
                  <div className="mt-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-gray-900">
                        ${PLAN_PRICES[billingCycle].elite.monthly}
                      </span>
                      <span className="text-sm text-gray-500">/month</span>
                    </div>
                    <div className="mt-1 text-[10px] text-gray-400">
                      Billed ${PLAN_PRICES[billingCycle].elite.billed} {billingCycle}
                    </div>
                  </div>

                  <button
                    onClick={() => window.open(PAYMENT_LINKS[billingCycle].elite, '_blank')}
                    className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-[#1C1C1E] py-2.5 text-xs font-semibold text-white transition hover:bg-gray-800"
                  >
                    Get elite <ArrowRight className="h-3.5 w-3.5" />
                  </button>

                  <div className="mt-5 space-y-3">
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <Zap className="h-4 w-4 flex-shrink-0 fill-current text-yellow-500" />
                      <span className="font-bold">9000 credits/month</span>
                    </div>
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <MonitorPlay className="h-4 w-4 flex-shrink-0 text-blue-500" />
                      <div className="flex items-center gap-1">
                        <span>≈ 40 mins Full AI video</span>
                        <Info className="h-3 w-3 text-gray-400" />
                      </div>
                    </div>
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <FolderOpen className="h-4 w-4 flex-shrink-0 text-blue-500" />
                      <div className="flex items-center gap-1">
                        <span>≈ 400 mins with Stock video</span>
                        <Info className="h-3 w-3 text-gray-400" />
                      </div>
                    </div>
                    <div className="flex gap-2.5 text-xs text-gray-600">
                      <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                      <span>Unlimited storage</span>
                    </div>
                    {FEATURES.paidCommon.map((feature, i) => (
                      <div key={i} className="flex gap-2.5 text-xs text-gray-600">
                        <feature.icon className={`h-4 w-4 flex-shrink-0 ${feature.color}`} />
                        <span>{feature.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Business Section */}
          <div className="mt-5 flex flex-col items-center justify-between gap-5 rounded-2xl border border-gray-200 p-5 lg:flex-row">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900">Business</h3>
              <h4 className="mt-1 text-2xl font-bold text-gray-900">Custom price</h4>
              <p className="mt-1 text-xs text-gray-500">
                For teams and organizations with advanced needs.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                <Crown className="h-4 w-4 fill-current text-yellow-500" />
                API Service
              </div>
              <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                <Crown className="h-4 w-4 fill-current text-blue-500" />
                Unlimited storage
              </div>
              <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                <Crown className="h-4 w-4 fill-current text-green-500" />
                Custom service
              </div>
            </div>

            <button className="flex cursor-pointer items-center gap-2 rounded-lg bg-[#1C1C1E] px-6 py-2.5 text-xs font-semibold text-white transition hover:bg-gray-800">
              Contact us <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <button className="inline-flex cursor-pointer items-center gap-2 text-xs text-gray-500 hover:text-gray-900">
              <HelpCircle className="h-3.5 w-3.5" />
              Learn more
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
